<template>
  <div
    class="card accordion position-relative"
    v-hide="{ hide: !!trainingPeriod.removed }"
    @hideComplete="hideComplete"
  >
    <header class="card-header" role="tab">
      <section class="mb-0 mt-0">
        <div
          class="accordion__heading"
          :class="{ collapsed: !show }"
          role="menu"
          aria-expanded="false"
          :aria-controls="'unit-trainingPeriod-' + trainingPeriod.id"
        >
          <div
            data-bs-toggle="collapse"
            :data-bs-target="'#unit-trainingPeriod-' + trainingPeriod.id"
            class="flex-grow-1"
          >
            <div class="d-flex align-items-md-center flex-column flex-md-row align-items-start">
              <div class="flex-grow-1 accordion__title">
                <span class="badge bg-info me-2">{{ trainingPeriod.code }}</span> {{ trainingPeriod.name }}
              </div>
              <div class="me-2" v-if="ectsCount || hoursCount || modulesCount || unitiesCount">
                <span class="badge bg-info me-2 mt-md-0 mt-2">
                  <FontAwesomeIcon icon="fa-light fa-books" v-if="unitiesCount" /> {{ unitiesCount }}
                  {{ $tc("studea_manager.unit.title", 2) }}</span
                >
                <span class="badge bg-info me-2 mt-md-0 mt-2"
                  ><FontAwesomeIcon icon="fa-light fa-books" v-if="modulesCount" /> {{ modulesCount }}
                  {{ $tc("studea_manager.module.title", 2) }}</span
                >
                <span class="badge bg-info me-2 mt-md-0 mt-2"
                  ><FontAwesomeIcon icon="fa-light fa-timer" v-if="hoursCount" /> {{ hoursCount }} h</span
                >
                <span class="badge bg-info me-2 mt-md-0 mt-2"
                  ><FontAwesomeIcon icon="fa-light fa-graduation-cap" v-if="ectsCount" /> {{ ectsCount }}
                  {{ $t("training.program.ects") }}</span
                >
              </div>
            </div>
          </div>
          <div class="d-flex align-items-center mt-2 mt-md-0">
            <button
              class="btn-icon editTrainingPeriod me-2"
              @click="edit"
              v-tooltip="{
                title: stringManager().globalTranslation('edit', 'training-period', 'studea_manager', 1, true),
              }"
            >
              <FontAwesomeIcon icon="fa-light fa-pen" />
            </button>
            <button
              class="btn-icon removeTrainingPeriod me-2"
              @click="remove"
              v-tooltip="{
                title: stringManager().globalTranslation('delete', 'training-period', 'studea_manager', 1, true),
              }"
            >
              <FontAwesomeIcon icon="fa-light fa-trash" />
            </button>
            <div
              class="icons"
              :class="{ collapsed: !show }"
              data-bs-toggle="collapse"
              :data-bs-target="'#unit-trainingPeriod-' + trainingPeriod.id"
            >
              <FontAwesomeIcon icon="fa-light fa-angle-down" />
            </div>
          </div>
        </div>
      </section>
    </header>
    <div
      :id="'unit-trainingPeriod-' + trainingPeriod.id"
      class="collapse"
      :class="{ show: show }"
      :aria-labelledby="'unit-trainingPeriod-' + trainingPeriod.id"
      data-bs-parent="#unit-trainingPeriod-accordion"
    >
      <div class="card-body accordion__body">
        <div class="d-flex flex-column justify-content-between h-100 position-relative">
          <div>
            <template v-if="trainingPeriod.trainingUnits && Object.keys(trainingPeriod.trainingUnits).length">
              <TrainingTrainingPeriodHeadingComponent />
              <VueDraggableNext
                :animation="200"
                :delay="200"
                :delay-on-touch-only="true"
                :list="trainingPeriod.trainingUnits"
                handle=".entity__grip"
                @change="sort"
              >
                <template v-for="unit in trainingPeriod.trainingUnits" :key="unit.id">
                  <TrainingUnitComponent
                    :isUnit="true"
                    :unit="unit"
                    @removed="removed"
                    @edit="editUnitSubject"
                    @hideComplete="hideComplete"
                  />
                </template>
              </VueDraggableNext>
            </template>
            <AlertBlock v-else :text="$t('training.program.alert.empty_unit')" type="info mb-0" />
          </div>
        </div>
      </div>
    </div>
    <BlockLoader :loading="loading || trainingPeriod.loading" />
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { trainingManager } from "@/services/training/training-manager";
import BlockLoader from "@/components/blocks/BlockLoader.vue";
import AlertBlock from "@/components/blocks/AlertBlock.vue";
import TrainingTrainingPeriodHeadingComponent from "@/components/training/model/TrainingTrainingPeriodHeadingComponent.vue";
import TrainingUnitComponent from "@/components/training/model/TrainingUnitComponent.vue";
import { VueDraggableNext } from "vue-draggable-next";
import { stringManager } from "@/services/utilities/string-manager";

export default {
  name: "TrainingTrainingPeriodComponent",
  components: {
    VueDraggableNext,
    TrainingUnitComponent,
    TrainingTrainingPeriodHeadingComponent: TrainingTrainingPeriodHeadingComponent,
    AlertBlock,
    BlockLoader,
    FontAwesomeIcon,
  },
  data() {
    return {
      loading: false,
    };
  },
  props: {
    trainingPeriod: {
      type: Object,
      required: true,
    },
    show: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["editTrainingPeriod", "editUnitSubject", "removed", "hideComplete"],
  computed: {
    unitiesCount() {
      return this.trainingPeriod.trainingUnits && Object.keys(this.trainingPeriod.trainingUnits).length
        ? Object.values(this.trainingPeriod.trainingUnits).filter(
            (unit) => unit.childTrainingUnits && !Object.keys(unit.childTrainingUnits).length,
          ).length
        : 0;
    },

    modulesCount() {
      let modulesCount = 0;
      if (this.trainingPeriod.trainingUnits && Object.keys(this.trainingPeriod.trainingUnits).length) {
        Object.values(this.trainingPeriod.trainingUnits).forEach((unit) => {
          modulesCount +=
            unit.childTrainingUnits && Object.keys(unit.childTrainingUnits).length
              ? Object.keys(unit.childTrainingUnits).length
              : 0;
        });
      }
      return modulesCount;
    },

    hoursCount() {
      return this.count("totalHours");
    },

    ectsCount() {
      return this.count("ectsCredit");
    },
  },
  methods: {
    stringManager() {
      return stringManager;
    },

    count(key) {
      let count = 0;
      if (this.trainingPeriod.trainingUnits && Object.keys(this.trainingPeriod.trainingUnits).length) {
        Object.values(this.trainingPeriod.trainingUnits).forEach((entity) => {
          count += entity[key] ? entity[key] : 0;
        });
      }
      return count;
    },

    sort(e) {
      if (e.moved) {
        const id = e.moved.element.id;
        const position = e.moved.newIndex;
        trainingManager.sort(id, this.$route.params.trainingId, position, "unit", "unit");
      }
    },

    edit() {
      this.$emit("editTrainingPeriod", { trainingPeriod: this.trainingPeriod });
    },

    editUnitSubject(payload) {
      this.$emit("editUnitSubject", { ...{ trainingPeriod: this.trainingPeriod }, ...payload });
    },

    async remove() {
      this.loading = true;
      const done = await trainingManager.remove(
        this.trainingPeriod.id,
        this.$route.params.trainingId,
        "training-period",
        "training-period",
      );
      if (done && done.success) {
        this.$emit("removed", { object: this.trainingPeriod, type: "training-period" });
      }
      this.loading = false;
    },

    hideComplete(payload) {
      let data;
      if (payload.type === "unit" || payload.type === "subject") {
        data = { ...payload, ...{ trainingPeriod: this.trainingPeriod } };
      } else {
        data = {
          object: this.trainingPeriod,
          type: "training-period",
        };
      }
      this.$emit("hideComplete", data);
    },

    removed(payload) {
      this.$emit("removed", { ...payload, ...{ trainingPeriod: this.trainingPeriod } });
    },
  },
};
</script>

<style lang="scss" scoped>
.accordion {
  &__card + &__card {
    margin-top: 15px;
  }

  &.card {
    overflow: hidden;
    .card-header {
      border-bottom: none;
      padding: 0;
      cursor: pointer;
    }
  }

  &__body {
    @include up-to-md {
      padding: 0;
    }
  }

  &__grip {
    margin-right: 18px;
    cursor: grab;
  }

  &__heading {
    padding: 20px;
    display: flex;
    position: relative;
    font-weight: 700;
    color: var(--primary-color);
    justify-content: space-between;
    align-items: center;
    background: $light-blue;

    @include dark-theme {
      color: $white;
      background: $m_color_12;
    }

    @include up-to-md {
      padding: 10px;
      align-items: flex-start;
      flex-direction: column;
    }

    .accordion-icon {
      margin-right: 8px;
      font-size: 16px;
    }

    .icons {
      @include up-to-md {
        position: absolute;
        right: 10px;
        bottom: 10px;
      }

      transform: rotate(180deg);
      &.collapsed {
        transform: rotate(0deg);
      }
    }
  }

  &__title {
    font-size: 16px;
  }
}
</style>
